import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import Icon from '@/components/Icon'

import { KeyAlternativeButton, KeyAlternativePopup, KeyboardButton, KeyboardPortal, KeyboardRow, KeyboardWrapper } from './Keyboard.styles'
import useKeyboardDialogOffset from './useKeyboardDialogOffset'

interface KeyboardProps {
  value?: string | null
  onChange: (value: string) => void
  maxLength?: number
  visible?: boolean
  numeric?: boolean
  offsetDialog?: boolean
  animateOnMount?: boolean
  onHeightChange?: (height: number) => void
  onEnter?: () => void
}

const letterRows = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', 'backspace'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'enter'],
  ['shift', 'z', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.', 'shift']
]

const numberRows = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'backspace'],
  ['-', '/', ':', ';', '(', ')', '$', '&', '@'],
  ['.', ',', '?', '!', "'", '"', '+', '#']
]

const KEY_ALTERNATIVES: Record<string, Record<string, string[]>> = {
  pl: {
    a: ['ą', 'á', 'à', 'â', 'ä', 'æ', 'ã', 'å'],
    c: ['ć', 'č', 'ç'],
    e: ['ę', 'é', 'è', 'ê', 'ë'],
    l: ['ł'],
    n: ['ń', 'ñ'],
    o: ['ó', 'ö', 'ô', 'ò', 'õ', 'ø'],
    s: ['ś', 'š'],
    z: ['ż', 'ź', 'ž']
  },
  en: {
    a: ['á', 'à', 'â', 'ä', 'æ', 'ã', 'å'],
    c: ['ç', 'ć', 'č'],
    e: ['é', 'è', 'ê', 'ë'],
    n: ['ñ', 'ń'],
    o: ['ó', 'ö', 'ô', 'ò', 'õ', 'ø'],
    s: ['ś', 'š'],
    z: ['ž', 'ź', 'ż']
  }
}

const getLanguageCode = (language?: string) => (language ?? 'en').split('-')[0].toLowerCase()

const getRootFontSize = () => Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16

const getAlternativePopupOffset = (button: HTMLElement, alternativesCount: number) => {
  const viewportPadding = 8
  const optionWidth = 3.4 * getRootFontSize()
  const popupWidth = alternativesCount * optionWidth
  const buttonRect = button.getBoundingClientRect()
  const centeredPopupLeft = buttonRect.left + buttonRect.width / 2 - popupWidth / 2
  const centeredPopupRight = centeredPopupLeft + popupWidth

  if (centeredPopupLeft < viewportPadding) {
    return viewportPadding - centeredPopupLeft
  }

  if (centeredPopupRight > window.innerWidth - viewportPadding) {
    return window.innerWidth - viewportPadding - centeredPopupRight
  }

  return 0
}

interface AlternativePopupState {
  key: string
  highlighted: string
  offsetX: number
}

const Keyboard = ({
  value,
  onChange,
  maxLength = 60,
  visible = true,
  numeric = false,
  offsetDialog = true,
  animateOnMount = true,
  onHeightChange,
  onEnter
}: KeyboardProps) => {
  const { i18n } = useTranslation()
  const [shift, setShift] = useState(!numeric && !(value ?? '').length)
  const [symbols, setSymbols] = useState(numeric)
  const [alternativePopup, setAlternativePopup] = useState<AlternativePopupState | null>(null)
  const keyboardRef = useRef<HTMLDivElement | null>(null)
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressOpenedRef = useRef(false)
  const rows = useMemo(() => (symbols ? numberRows : letterRows), [symbols])
  const alternatives = KEY_ALTERNATIVES[getLanguageCode(i18n.resolvedLanguage ?? i18n.language)] ?? KEY_ALTERNATIVES.en

  useKeyboardDialogOffset(offsetDialog && visible, keyboardRef)

  useLayoutEffect(() => {
    if (!visible || !keyboardRef.current || !onHeightChange) {
      return
    }

    onHeightChange(keyboardRef.current.getBoundingClientRect().height)
  }, [onHeightChange, rows, visible])

  useEffect(() => {
    if (visible) {
      setSymbols(numeric)
      setShift(!numeric && !(value ?? '').length)
      setAlternativePopup(null)
    }
  }, [numeric, visible])

  useEffect(() => {
    if (visible && !numeric && !symbols && !(value ?? '').length) {
      setShift(true)
    }
  }, [numeric, symbols, value, visible])

  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
      }
    }
  }, [])

  const handleKeyPress = (key: string) => {
    const currentValue = value ?? ''
    setAlternativePopup(null)

    if (key === 'backspace') {
      const nextValue = currentValue.slice(0, -1)
      onChange(nextValue)
      if (!numeric && !nextValue.length) {
        setShift(true)
      }
      return
    }

    if (key === 'shift') {
      setShift(current => !current)
      return
    }

    if (key === '?123') {
      setSymbols(true)
      return
    }

    if (key === 'abc') {
      setSymbols(false)
      return
    }

    if (key === 'enter') {
      onEnter?.()
      return
    }

    if (key === 'space') {
      if (currentValue.length < maxLength) {
        onChange(`${currentValue} `)
      }
      return
    }

    if (currentValue.length >= maxLength) {
      return
    }

    const nextKey = shift && key.length === 1 ? key.toUpperCase() : key
    onChange(currentValue + nextKey)

    if (shift && /^[a-z]$/.test(key)) {
      setShift(false)
    }
  }

  const handleAlternativeKeyPress = (key: string) => {
    const currentValue = value ?? ''
    if (currentValue.length >= maxLength) {
      return
    }

    const nextKey = shift ? key.toUpperCase() : key
    onChange(currentValue + nextKey)
    setAlternativePopup(null)

    if (shift) {
      setShift(false)
    }
  }

  const getKeyAlternatives = (key: string) => alternatives[key] ?? []

  const getAlternativeFromPoint = (event: React.PointerEvent, key: string) => {
    const target = document.elementFromPoint(event.clientX, event.clientY)
    if (!(target instanceof Element)) {
      return null
    }

    const option = target.closest<HTMLElement>('[data-key-alternative]')
    if (!option || option.dataset.keyAlternativeSource !== key) {
      return null
    }

    return option.dataset.keyAlternative ?? null
  }

  const handleKeyPointerDown = (event: React.PointerEvent<HTMLButtonElement>, key: string) => {
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    longPressOpenedRef.current = false

    const keyAlternatives = getKeyAlternatives(key)
    if (!keyAlternatives.length || symbols) {
      return
    }

    const sourceButton = event.currentTarget
    longPressTimerRef.current = setTimeout(() => {
      longPressOpenedRef.current = true
      setAlternativePopup({
        key,
        highlighted: keyAlternatives[0],
        offsetX: getAlternativePopupOffset(sourceButton, keyAlternatives.length)
      })
    }, 420)
  }

  const handleKeyPointerMove = (event: React.PointerEvent<HTMLButtonElement>, key: string) => {
    if (!longPressOpenedRef.current || alternativePopup?.key !== key) {
      return
    }

    const highlighted = getAlternativeFromPoint(event, key)
    if (highlighted) {
      setAlternativePopup(current => (current?.key === key ? { ...current, highlighted } : current))
    }
  }

  const handleKeyPointerUp = (event: React.PointerEvent<HTMLButtonElement>, key: string) => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    if (longPressOpenedRef.current) {
      if (alternativePopup?.key === key) {
        const highlighted = getAlternativeFromPoint(event, key) ?? alternativePopup.highlighted
        handleAlternativeKeyPress(highlighted)
      }
      longPressOpenedRef.current = false
      setAlternativePopup(null)
      return
    }

    handleKeyPress(key)
  }

  const handleKeyPointerCancel = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    longPressOpenedRef.current = false
    setAlternativePopup(null)
  }

  const getKeyLabel = (key: string) => {
    if (key === 'backspace') return <Icon name="key-backspace" />
    if (key === 'enter') return <Icon name="key-enter" />
    if (key === 'shift') return <Icon name="key-shift" />
    if (key === 'space') return ''
    if (key === 'abc') return 'ABC'
    if (shift && key.length === 1 && /[a-z]/.test(key)) return key.toUpperCase()
    return key
  }

  const keyboardRoot = document.querySelector('#modal') ?? document.body

  return createPortal(
    <AnimatePresence>
      {visible && (
        <KeyboardPortal
          ref={keyboardRef}
          data-element="keyboard-portal"
          as={motion.div}
          onPointerDown={event => event.preventDefault()}
          onContextMenu={event => event.preventDefault()}
          initial={animateOnMount ? { y: '100%', opacity: 0 } : false}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <KeyboardWrapper>
            {rows.map((row, rowIndex) => (
              <KeyboardRow key={rowIndex} $offset={rowIndex === 1}>
                {row.map((key, keyIndex) => (
                  <KeyboardButton
                    key={`${rowIndex}-${key}-${keyIndex}`}
                    type="button"
                    onPointerDown={event => handleKeyPointerDown(event, key)}
                    onPointerMove={event => handleKeyPointerMove(event, key)}
                    onPointerUp={event => handleKeyPointerUp(event, key)}
                    onPointerCancel={handleKeyPointerCancel}
                    $action={['backspace', 'enter', 'shift', 'abc'].includes(key)}
                    $enter={key === 'enter'}
                  >
                    {getKeyLabel(key)}
                    <AnimatePresence>
                      {alternativePopup?.key === key && (
                        <KeyAlternativePopup
                          as={motion.div}
                          $offsetX={alternativePopup.offsetX}
                          initial={{ opacity: 0, '--popup-y': '-10px' }}
                          animate={{ opacity: 1, '--popup-y': '0px' }}
                          exit={{ opacity: 0, '--popup-y': '-10px' }}
                          transition={{ duration: 0.12, ease: 'easeOut' }}
                        >
                          {getKeyAlternatives(key).map(alternative => (
                            <KeyAlternativeButton
                              key={alternative}
                              role="button"
                              data-key-alternative-source={key}
                              data-key-alternative={alternative}
                              $active={alternativePopup.highlighted === alternative}
                            >
                              {shift ? alternative.toUpperCase() : alternative}
                            </KeyAlternativeButton>
                          ))}
                        </KeyAlternativePopup>
                      )}
                    </AnimatePresence>
                  </KeyboardButton>
                ))}
              </KeyboardRow>
            ))}
            <KeyboardRow $offset={true}>
              <KeyboardButton
                type="button"
                onPointerDown={event => event.preventDefault()}
                onClick={() => handleKeyPress(symbols ? 'abc' : '?123')}
                $action
              >
                {symbols ? 'ABC' : '?123'}
              </KeyboardButton>
              <KeyboardButton type="button" onPointerDown={event => event.preventDefault()} onClick={() => handleKeyPress('space')} $wide />
              <KeyboardButton
                type="button"
                onPointerDown={event => event.preventDefault()}
                onClick={() => handleKeyPress(symbols ? 'abc' : '?123')}
                $action
              >
                {symbols ? 'ABC' : '?123'}
              </KeyboardButton>
            </KeyboardRow>
          </KeyboardWrapper>
        </KeyboardPortal>
      )}
    </AnimatePresence>,
    keyboardRoot
  )
}

Keyboard.displayName = 'Keyboard'

export default Keyboard
